import { useState, useEffect, useContext } from 'react'
import Image from 'next/image'

import Select from '/src/components/@common.blocks/Select'
import FormField from '/src/components/@common.blocks/FormField'
import Checkbox from '/src/components/@common.blocks/Checkbox'
import Text from '/src/components/@common.blocks/Text'
import Input from '/src/components/@common.blocks/Input'
import Button from '/src/components/@common.blocks/Button'
import DeviceContext from '/src/contexts/DeviceContext'
import onMenuOpen from '/src/helpers/onMenuOpen'
import Grid from '/src/components/@common.blocks/Grid'

import arrowDownIcon from '/public/ui-icons/arrow-down.svg'

const sortOptions = [
  {
    title: 'Rank: low to high',
    value: 'rank_asc',
  },
  {
    title: 'Rank: high to low',
    value: 'rank_desc',
  },
]

const FormFilter = ({
  traits,
  query,
  onQueryChange,
  onMenuClose,
  onSubmit,
}) => {
  const [openTraits, setOpenTraits] = useState([])
  const [searchTraits, setSearchTraits] = useState('')

  const onTraitOpen = (e, trait) => {
    setOpenTraits((prevState) => {
      if (prevState.indexOf(trait) >= 0) {
        return prevState.filter((el) => el !== trait)
      } else {
        return [...prevState, trait]
      }
    })
  }

  const onPropCheck = (e, key, value) => {
    const filterTraits = query.traits ? JSON.parse(query.traits) : {}
    let values = filterTraits[key] || []
    values =
      values.indexOf(value) >= 0
        ? values.filter((el) => el !== value)
        : [...values, value]

    let res = { ...filterTraits }
    if (values.length === 0) {
      delete res[key]
    } else {
      res[key] = values
    }

    if (Object.keys(res).length === 0) {
      res = undefined
    }

    onQueryChange({}, 'traits', JSON.stringify(res))
  }

  const isPropChecked = (key, value) => {
    const filterTraits = query.traits ? JSON.parse(query.traits) : {}
    return filterTraits[key] && filterTraits[key].indexOf(value) >= 0
  }

  const traitList = Object.keys(traits)
    .map((traitName) => ({
      traitName,
      traitValues: traits[traitName]
        .filter((traitValues) => {
          if (traitValues.type === 'range') return false
          if (!searchTraits) return true
          return new RegExp(searchTraits, 'i').test(traitValues.value)
        })
        .sort((a, b) => a.count - b.count),
    }))
    .filter((item) => item.traitValues.length > 0)

  const rankMin = query.rank?.split('-')[0] || undefined
  const rankMax = query.rank?.split('-')[1] || undefined

  return (
    <>
      <div className='assets-filter__header'>
        <Text component='span' size='xl' weight='bold'>
          Filter
        </Text>
        {onMenuClose && (
          <div
            className='assets-filter__close-btn'
            onClick={() => onMenuClose()}
          >
            <div />
            <div />
          </div>
        )}
      </div>
      <form className='assets-filter__form'>
        <FormField title='Sort'>
          <Select
            name='sort'
            value={query.sort}
            options={sortOptions}
            onSelect={(e) => onQueryChange(e, e.target.name, e.target.value)}
          />
        </FormField>
        <FormField title='Rank'>
          <Grid gutter={2}>
            <Grid.Col sm={6} xs={6}>
              <Input
                name='rankMin'
                value={rankMin}
                placeholder='Min'
                type='number'
                onChange={(e) =>
                  onQueryChange(e, e.target.name, e.target.value)
                }
              />
            </Grid.Col>
            <Grid.Col sm={6} xs={6}>
              <Input
                name='rankMax'
                value={rankMax}
                placeholder='Max'
                type='number'
                onChange={(e) =>
                  onQueryChange(e, e.target.name, e.target.value)
                }
              />
            </Grid.Col>
          </Grid>
        </FormField>
        <FormField title='Properties'>
          <Input
            placeholder='Search traits'
            value={searchTraits}
            onChange={(e) => setSearchTraits(e.target.value)}
            onClear={(e) => setSearchTraits('')}
          />
        </FormField>

        <ul className='assets-filter__form-traits'>
          {traitList.map((trait) => {
            const isOpen = openTraits.indexOf(trait.traitName) >= 0
            const className =
              'assets-filter__traits-item' +
              (isOpen ? ' assets-filter__traits-item--open' : '')
            return (
              <li key={trait.traitName} className={className}>
                <div
                  className='assets-filter__trait'
                  onClick={(e) => onTraitOpen(e, trait.traitName)}
                >
                  <span>{trait.traitName}</span>
                  <Image
                    src={arrowDownIcon}
                    width={12}
                    height={12}
                    alt='arrow'
                  />
                </div>
                <ul
                  className={`assets-filter__trait-props${
                    isOpen ? ' assets-filter__trait-props--open' : ''
                  }`}
                >
                  {trait.traitValues.map((traitValues, key) => {
                    return (
                      <li key={key}>
                        {traitValues.type === 'range' ? null : (
                          <>
                            <Checkbox
                              checked={isPropChecked(
                                trait.traitName,
                                traitValues.value
                              )}
                              onChange={(e) =>
                                onPropCheck(
                                  e,
                                  trait.traitName,
                                  traitValues.value
                                )
                              }
                            >
                              {traitValues.value}
                            </Checkbox>
                            <Text component='span' color='light-gray'>
                              {traitValues.count}
                            </Text>
                          </>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </li>
            )
          })}
        </ul>
        {onSubmit && (
          <div className='assets-filter__form-submit'>
            <Button color='primary' onClick={onSubmit}>
              Done
            </Button>
          </div>
        )}
      </form>
    </>
  )
}

const AssetsFilter = ({
  traits = {},
  supply,
  onChange,
  query,
  onQueryChange,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)

  const { isMobile } = useContext(DeviceContext)

  useEffect(() => {
    onMenuOpen(menuOpen)
    return () => onMenuOpen(false)
  }, [menuOpen])

  return isMobile ? (
    <>
      <Button
        className='assets-filter__toggle-mobile'
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <img src='/ui-icons/sort.svg' alt='filter' />
        Filter
      </Button>
      {menuOpen && (
        <div className='assets-filter-mobile'>
          <FormFilter
            supply={supply}
            traits={traits}
            onChange={onChange}
            onMenuClose={() => setMenuOpen(false)}
            onSubmit={() => setMenuOpen(false)}
            query={query}
            onQueryChange={onQueryChange}
          />
        </div>
      )}
    </>
  ) : (
    <div className='assets-filter'>
      <FormFilter
        supply={supply}
        traits={traits}
        onChange={onChange}
        query={query}
        onQueryChange={onQueryChange}
      />
    </div>
  )
}

export default AssetsFilter
